import React, { useEffect, useState, useRef } from 'react';
import { Form, Button, Row, Col, Table, Badge } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import imageCompression from 'browser-image-compression';
import VotingCategoryPlayers, { VotingPlayer } from './VotingCategoryPlayers';
import VotingRemoveModal from './VotingRemoveModal';
import { Fixture, listFixtures } from '../../../api/tournament/fixturesService';
import { listPlayers } from '../../../api/tournament/playerService';
import { listTeams } from '../../../api/tournament/teamService';
import { getAllAgeCategoriesPaginated } from '../../../api/tournament/ageCategoryService';
import { formatApiDateDisplay, formatApiTimeDisplay, formatDateDisplay } from '../../../utils/formatUtils';
import secureApi from '../../../utils/secureApi';
import {
  createAwardCategory,
  createCampaignQuestion,
  createVotingCampaign,
  EngagementAwardCategory,
  EngagementCampaign,
  EngagementQuestion,
  getEngagementSetting,
  listAwardCategories,
  listCampaignQuestions,
  listVotingCampaigns,
  updateVotingCampaign,
  updateCampaignQuestion,
} from '../../../api/sflTvEngagement/engagementService';

interface VotingManagementProps {
  tournamentId?: string;
  seasonId?: string;
  tournamentSports?: string;
  ageCategoryOptionsList?: { value: string; label: string }[];
}

type DropdownOption = {
  value: string;
  label: string;
  __isNew__?: boolean;
  fixture?: Fixture;
};

type VotingFilterValue = 'tournaments' | 'matches';
type VotingViewMode = 'list' | 'add' | 'players' | 'category' | 'add-match' | 'match-detail';

// Voting status type for each category
type VotingStatus = 'Draft' | 'Scheduled' | 'Upcoming' | 'Live' | 'Locked' | 'Completed';
type SubmissionStatus = 'open' | 'not_open' | 'closed' | string;

// Shape of a saved voting category row
interface VotingCategoryItem {
  id: string;
  campaignId?: string;
  awardCategoryId?: string;
  matchId?: string;
  name: string;
  level: string;           // 'Player' | 'Team'
  ageCategory: string;     // e.g. 'U9'
  startDate: string;       // dd/mm/yyyy display string
  endDate: string;
  startTime: string;
  endTime: string;
  sortNumber: string;
  ageCategoryValue: string;
  votingStatus: VotingStatus;
  categoryEnabled: boolean;
  submissionStatus?: SubmissionStatus;
  canSubmit?: boolean;
  submissionMessage?: string;
  bannerImagePreview?: string;
  videoUrl?: string;
  config?: Record<string, any>;
}

type VotingFilterState<T> = Record<VotingFilterValue, T>;
type VotingControlMode = 'on' | 'off' | 'mixed';

type VotingControlState = {
  mode: VotingControlMode;
  enabledCount: number;
  totalCount: number;
  failedIds: string[];
};

interface MatchVotingItem {
  id: string;
  campaignId?: string;
  team: string;
  matchValue: string;
  ageCategory: string;
  ageCategoryValue: string;
  matchDate: string;
  startTime: string;
  endTime: string;
  votingStatus: VotingStatus;
  categoryEnabled: boolean;
}

const filterOptions: DropdownOption[] = [
  { value: 'tournaments', label: 'Tournament' },
  { value: 'matches', label: 'Match' },
];

const playerTeamLevelOptions: DropdownOption[] = [
  { value: 'player', label: 'Player' },
  { value: 'team', label: 'Team' },
];

const getFixtureId = (fixture: Fixture): string => fixture._id;

const getFixtureSideName = (side: any, fallbackTeam?: any): string => {
  if (side?.team && typeof side.team === 'object') return side.team.name || side.team.shortName || '';
  if (Array.isArray(side?.players) && side.players.length > 0) {
    return side.players.map((player: any) => player?.name).filter(Boolean).join(' / ');
  }
  return fallbackTeam?.name || fallbackTeam?.shortName || '';
};

const getFixtureMatchLabel = (fixture: Fixture): string => {
  const firstSide = getFixtureSideName((fixture as any).side1 || (fixture as any).team1Info, fixture.teamA);
  const secondSide = getFixtureSideName((fixture as any).side2 || (fixture as any).team2Info, fixture.teamB);
  const teamsLabel = [firstSide, secondSide].filter(Boolean).join(' vs ');
  return teamsLabel || `Match ${fixture.matchNum || ''}`.trim();
};

const toDateInputValue = (value?: string): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(0, 10);
};

const toTimeInputValue = (value?: string): string => {
  if (!value) return '';
  const trimmedValue = value.trim();
  const twentyFourHourMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})/);
  const meridiemMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (meridiemMatch) {
    let hours = Number(meridiemMatch[1]);
    const minutes = meridiemMatch[2];
    const meridiem = meridiemMatch[3].toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  if (twentyFourHourMatch) {
    return `${twentyFourHourMatch[1].padStart(2, '0')}:${twentyFourHourMatch[2]}`;
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) return '';
  return `${String(parsedDate.getUTCHours()).padStart(2, '0')}:${String(parsedDate.getUTCMinutes()).padStart(2, '0')}`;
};

const mapFixtureToMatchOption = (fixture: Fixture): DropdownOption => ({
  value: getFixtureId(fixture),
  label: getFixtureMatchLabel(fixture),
  fixture,
});

const toCampaignDateTime = (date: string, time: string): string => {
  const safeTime = time || '00:00';
  return new Date(`${date}T${safeTime}:00`).toISOString();
};

const getQuestionStatusForDates = (startTime?: string, lockTime?: string): string => {
  const now = Date.now();
  const startMs = startTime ? new Date(startTime).getTime() : Number.NaN;
  const lockMs = lockTime ? new Date(lockTime).getTime() : Number.NaN;

  if (!Number.isNaN(startMs) && startMs > now) return 'scheduled';
  if (!Number.isNaN(lockMs) && lockMs <= now) return 'locked';
  return 'active';
};

const getQuestionVotingLockStatus = (questionStatus: string): 'active' | 'locked' => (
  questionStatus === 'locked' ? 'locked' : 'active'
);

const getQuestionVotingLockStatusFromCategory = (category: VotingCategoryItem): 'active' | 'locked' => (
  category.votingStatus === 'Live' ? 'active' : 'locked'
);

const getVotingStatus = (status?: string): VotingStatus => {
  const rawStatus = status?.toLowerCase();
  if (rawStatus === 'draft') return 'Draft';
  if (rawStatus === 'scheduled') return 'Scheduled';
  if (rawStatus === 'live' || rawStatus === 'active') return 'Live';
  if (rawStatus === 'locked') return 'Locked';
  if (rawStatus === 'completed' || rawStatus === 'complete' || rawStatus === 'archived') return 'Completed';
  return 'Upcoming';
};

const getCampaignStatus = (campaign?: EngagementCampaign | null): VotingStatus => getVotingStatus(campaign?.status);

const getCampaignId = (campaign?: EngagementCampaign | null): string => campaign?._id ?? campaign?.id ?? '';
const getCampaignSeasonId = (campaign?: EngagementCampaign | null): string => campaign?.seasonId ?? campaign?.config?.seasonId ?? '';
const isLiveCampaign = (campaign: EngagementCampaign): boolean => ['live', 'active'].includes(campaign.status?.toLowerCase() || '');

const getDeletedQuestionPayload = (category: VotingCategoryItem) => ({
  config: {
    ...(category.config ?? {}),
    deleted: true,
    deletedAt: new Date().toISOString(),
  },
});

const isVotingCategoryReadOnly = (status: VotingStatus): boolean => ['Live', 'Locked', 'Completed'].includes(status);

const getVotingStatusFromSubmission = (
  submissionStatus: SubmissionStatus | undefined,
  fallbackStatus: VotingStatus
): VotingStatus => {
  const normalizedSubmissionStatus = submissionStatus?.toLowerCase();
  if (normalizedSubmissionStatus === 'open') return 'Live';
  if (normalizedSubmissionStatus === 'not_open') {
    return fallbackStatus === 'Scheduled' ? 'Scheduled' : 'Upcoming';
  }
  if (normalizedSubmissionStatus === 'closed') {
    return fallbackStatus === 'Completed' ? 'Completed' : 'Locked';
  }
  return fallbackStatus;
};

const canEditCategoryPlayers = (category: VotingCategoryItem): boolean => {
  if (category.votingStatus === 'Live') return true;
  if (typeof category.canSubmit === 'boolean') return category.canSubmit;
  if (category.submissionStatus) return category.submissionStatus.toLowerCase() === 'open';
  return !['Locked', 'Completed'].includes(category.votingStatus);
};

const getMatchRowKey = (match: MatchVotingItem): string => match.campaignId || match.id;

const isCategoryForMatch = (category: VotingCategoryItem, match: MatchVotingItem): boolean => (
  match.campaignId ? category.campaignId === match.campaignId : category.matchId === match.id
);

const getCampaignScope = (campaign?: EngagementCampaign | null): string => (
  campaign?.scope ?? campaign?.config?.scope ?? campaign?.config?.target ?? ''
).toLowerCase();

const isVisibleSeasonVotingCampaign = (campaign: EngagementCampaign): boolean => (
  campaign.status?.toLowerCase() !== 'archived' &&
  getCampaignScope(campaign) === 'season'
);

const isVisibleMatchVotingCampaign = (campaign: EngagementCampaign): boolean => (
  campaign.status?.toLowerCase() !== 'archived' &&
  getCampaignScope(campaign) === 'match'
);

const isVotingCampaignForSeason = (campaign: EngagementCampaign, seasonId: string): boolean => (
  campaign.type === 'voting' &&
  getCampaignSeasonId(campaign) === seasonId &&
  (getCampaignScope(campaign) === 'season' || getCampaignScope(campaign) === 'match')
);

const getLiveVotingCampaigns = (campaigns: EngagementCampaign[], seasonId: string): EngagementCampaign[] => {
  const byId = new Map<string, EngagementCampaign>();
  campaigns
    .filter(campaign => isVotingCampaignForSeason(campaign, seasonId) && isLiveCampaign(campaign))
    .forEach(campaign => {
      const campaignId = getCampaignId(campaign);
      if (campaignId) byId.set(campaignId, campaign);
    });
  return Array.from(byId.values());
};

const getVotingControlState = (campaigns: EngagementCampaign[], failedIds: string[] = []): VotingControlState => {
  const totalCount = campaigns.length;
  const enabledCount = campaigns.filter(campaign => campaign.enabled !== false).length;
  const mode: VotingControlMode = totalCount === 0 || enabledCount === 0
    ? 'off'
    : enabledCount === totalCount
      ? 'on'
      : 'mixed';

  return {
    mode,
    enabledCount,
    totalCount,
    failedIds,
  };
};

const listAllVotingCampaigns = async (params: Parameters<typeof listVotingCampaigns>[0]): Promise<EngagementCampaign[]> => {
  const firstPage = await listVotingCampaigns({ ...params, page: 1, limit: params?.limit ?? 100 });
  const campaigns = [...(firstPage.campaigns ?? [])];
  let currentPage = firstPage.currentPage || 1;
  let totalPages = firstPage.totalPages || 1;
  let hasNext = Boolean(firstPage.hasNext);

  while (hasNext || currentPage < totalPages) {
    const response = await listVotingCampaigns({ ...params, page: currentPage + 1, limit: params?.limit ?? 100 });
    campaigns.push(...(response.campaigns ?? []));
    currentPage = response.currentPage || currentPage + 1;
    totalPages = response.totalPages || totalPages;
    hasNext = Boolean(response.hasNext);
  }

  return campaigns;
};

const mapCampaignToMatch = (campaign: EngagementCampaign): MatchVotingItem => {
  const config = campaign.config ?? {};
  const matchId = config.matchId ?? '';

  return {
    id: matchId || getCampaignId(campaign),
    campaignId: getCampaignId(campaign),
    team: config.matchLabel ?? campaign.name ?? 'Match',
    matchValue: matchId,
    ageCategory: config.ageCategoryLabel ?? '',
    ageCategoryValue: config.ageCategoryId ?? '',
    matchDate: config.matchDate ? formatDateDisplay(config.matchDate) : formatApiDateDisplay(campaign.startTime),
    startTime: config.matchStartTime ?? formatApiTimeDisplay(campaign.startTime),
    endTime: config.matchEndTime ?? formatApiTimeDisplay(campaign.endTime),
    votingStatus: getCampaignStatus(campaign),
    categoryEnabled: campaign.enabled !== false,
  };
};

const mapQuestionToCategory = (question: EngagementQuestion, campaign: EngagementCampaign): VotingCategoryItem => {
  const campaignConfig = campaign.config ?? {};
  const questionConfig = question.config ?? {};
  const ageCategoryLabel = questionConfig.ageCategoryLabel ?? campaignConfig.ageCategoryLabel ?? campaignConfig.ageCategory ?? '';
  const ageCategoryValue = questionConfig.ageCategoryId ?? campaignConfig.ageCategoryId ?? campaignConfig.ageCategoryValue ?? ageCategoryLabel;
  const level = questionConfig.playerTeamLevel ?? campaignConfig.playerTeamLevel ?? campaignConfig.level ?? 'Player';
  const levelLabel = typeof level === 'string' && level.toLowerCase() === 'team' ? 'Team' : 'Player';
  const matchId = question.matchId ?? questionConfig.matchId ?? campaignConfig.matchId;
  const questionStartTime = question.startTime ?? questionConfig.startTime ?? campaign.startTime;
  const questionEndTime = question.lockTime ?? campaign.endTime;
  const derivedQuestionStatus = questionStartTime || questionEndTime
    ? getQuestionStatusForDates(questionStartTime, questionEndTime)
    : question.status ?? campaign.status;
  const fallbackVotingStatus = getVotingStatus(derivedQuestionStatus);
  const votingStatus = getVotingStatusFromSubmission(question.submissionStatus, fallbackVotingStatus);

  return {
    id: getQuestionId(question),
    campaignId: getCampaignId(campaign),
    awardCategoryId: question.awardCategoryId ?? questionConfig.awardCategoryId ?? undefined,
    matchId,
    name: question.question ?? questionConfig.awardCategoryLabel ?? campaign.name ?? '',
    level: levelLabel,
    ageCategory: ageCategoryLabel,
    startDate: formatApiDateDisplay(questionStartTime),
    endDate: formatApiDateDisplay(questionEndTime),
    startTime: formatApiTimeDisplay(questionStartTime),
    endTime: formatApiTimeDisplay(questionEndTime),
    sortNumber: String(question.sortNo ?? questionConfig.sortNo ?? questionConfig.sortNumber ?? ''),
    ageCategoryValue,
    votingStatus,
    categoryEnabled: question.enabled !== false,
    submissionStatus: question.submissionStatus,
    canSubmit: question.canSubmit,
    submissionMessage: question.submissionMessage,
    bannerImagePreview: questionConfig.bannerImage ?? campaignConfig.bannerImage ?? campaign.presentation?.bannerImage ?? campaign.presentation?.bannerImagePreview,
    videoUrl: questionConfig.videoUrl ?? campaignConfig.videoUrl ?? campaign.presentation?.videoUrl,
    config: questionConfig,
  };
};

const getQuestionId = (question?: EngagementQuestion | null): string => question?._id ?? question?.id ?? '';

const getAwardCategoryId = (awardCategory: EngagementAwardCategory): string => awardCategory._id ?? awardCategory.id ?? awardCategory.code ?? '';

const getAwardCategoryName = (awardCategory: EngagementAwardCategory): string => awardCategory.name ?? awardCategory.title ?? awardCategory.label ?? awardCategory.code ?? 'Unnamed Award Category';

const mapAwardCategoryToOption = (awardCategory: EngagementAwardCategory): DropdownOption => ({
  value: getAwardCategoryId(awardCategory),
  label: getAwardCategoryName(awardCategory),
});

const mapAgeCategoryToOption = (ageCategory: any): DropdownOption | null => {
  if (!ageCategory) return null;

  if (typeof ageCategory === 'string') {
    return { value: ageCategory, label: ageCategory };
  }

  const value = ageCategory._id ?? ageCategory.id ?? ageCategory.value ?? ageCategory.code ?? ageCategory.ageCategory;
  const label = ageCategory.ageCategory ?? ageCategory.name ?? ageCategory.label ?? ageCategory.title ?? ageCategory.code ?? value;

  return value && label ? { value: String(value), label: String(label) } : null;
};

const getUniqueOptions = (options: DropdownOption[]): DropdownOption[] => {
  const seen = new Set<string>();
  return options.filter(option => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const getVotingBannerCompressionOptions = (file: File) => {
  const sizeKB = file.size / 1024;
  if (sizeKB < 120) return null;
  return { maxSizeMB: 0.15, maxWidthOrHeight: 1600, initialQuality: 0.92, useWebWorker: true };
};

const uploadVotingCategoryBannerImage = async (file: File): Promise<string> => {
  let processedFile = file;
  const options = getVotingBannerCompressionOptions(file);
  if (options) {
    processedFile = await imageCompression(file, options);
  }

  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const ext = processedFile.type.split('/')[1] || 'jpg';
  const fileName = `age_category_${Date.now()}.${ext}`;
  const context = `live-matches-banners/${dd}-${mm}-${yy}`;

  const presignRes = await secureApi.post<any>(
    '/api/v1/general/presigned-url',
    {
      fileName,
      contentType: processedFile.type,
      context,
    }
  );

  const presignData = presignRes?.data || presignRes;
  const presignedUrl = presignData?.presignedUrl;
  const s3Key = presignData?.key;

  if (!presignedUrl || !s3Key) {
    throw new Error('Failed to get presigned URL');
  }

  const uploadRes = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processedFile.type },
    body: processedFile,
  });

  if (!uploadRes.ok) {
    throw new Error('Failed to upload image to S3');
  }

  return s3Key;
};

const getPlayerName = (player: any): string => {
  const firstName = player?.playerInfo?.firstName ?? player?.firstName ?? player?.firstname ?? '';
  const lastName = player?.playerInfo?.lastName ?? player?.lastName ?? player?.lastname ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const resolvedName = player?.playerInfo?.name ?? player?.name ?? player?.playerName ?? player?.fullName ?? fullName;
  return resolvedName || 'Unnamed Player';
};

const getPlayerTeamName = (player: any): string => {
  return player?.teamName ?? player?.team?.name ?? player?.team?.teamName ?? player?.playerSeasonInfo?.team?.name ?? player?.playerSeasonInfo?.teamName ?? '-';
};

const mapApiPlayerToVotingPlayer = (player: any): VotingPlayer => ({
  id: player?.playerInfo?._id ?? player?._id ?? player?.id ?? player?.playerId ?? '',
  name: getPlayerName(player),
  teamName: getPlayerTeamName(player),
  profileImage: player?.playerInfo?.profilePic ?? player?.profileImage ?? player?.profilePicture ?? player?.profilePic ?? player?.image,
});

const getTeamName = (team: any): string => {
  const teamData = team?.team || team;
  return teamData?.name ?? teamData?.teamName ?? teamData?.clubName ?? teamData?.teamInfo?.name ?? 'Unnamed Team';
};

const mapApiTeamToVotingPlayer = (team: any): VotingPlayer => ({
  id: team?.team?._id ?? team?._id ?? team?.id ?? team?.teamId ?? '',
  name: getTeamName(team),
  teamName: getTeamName(team),
  profileImage: team?.team?.logo ?? team?.logo ?? team?.teamLogo ?? team?.image ?? team?.profileImage,
});

const mapQuestionOptionsToPlayers = (question?: EngagementQuestion): VotingPlayer[] => {
  if (!question?.options) return [];
  return question.options.map(option => ({
    id: option.id,
    name: option.label,
    teamName: option.meta?.team ?? '-',
    profileImage: option.meta?.photoUrl,
  }));
};

export const getApiErrorMessage = (error: any, fallback: string): string => {
  const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
  if (backendMessage) return backendMessage;
  if (error?.response?.status === 423) return 'Engagement is disabled at season level.';
  return error?.message || fallback;
};

const buildPlayersQuestionPayload = (category: VotingCategoryItem, players: VotingPlayer[]) => ({
  question: category.name,
  type: 'single_choice' as const,
  status: getQuestionVotingLockStatusFromCategory(category),
  options: players.map(player => ({
    id: player.id,
    label: player.name,
    meta: {
      team: player.teamName,
      photoUrl: player.profileImage,
    },
  })),
  ...(category.awardCategoryId ? { awardCategoryId: category.awardCategoryId } : {}),
  config: {
    source: 'voting-category-players',
    categoryId: category.id,
    campaignId: category.campaignId,
    awardCategoryId: category.awardCategoryId || '',
    ageCategoryId: category.ageCategoryValue,
    ageCategoryLabel: category.ageCategory,
  },
});

// Badge colour map
const statusBadgeStyle: Record<VotingStatus, React.CSSProperties> = {
  Draft: {
    backgroundColor: '#F8FAFC',
    color: '#475569',
    border: '1px solid #CBD5E1',
  },
  Scheduled: {
    backgroundColor: '#FFF7ED',
    color: '#C2410C',
    border: '1px solid #FDBA74',
  },
  Upcoming: {
    backgroundColor: '#FFF7ED',
    color: '#C2410C',
    border: '1px solid #FDBA74',
  },
  Live: {
    backgroundColor: '#F0FDF4',
    color: '#15803D',
    border: '1px solid #86EFAC',
  },
  Locked: {
    backgroundColor: '#FEF2F2',
    color: '#B91C1C',
    border: '1px solid #FCA5A5',
  },
  Completed: {
    backgroundColor: '#EFF6FF',
    color: '#1D4ED8',
    border: '1px solid #93C5FD',
  },
};

const VotingManagement: React.FC<VotingManagementProps> = ({
  tournamentId,
  seasonId,
  tournamentSports,
  ageCategoryOptionsList,
}) => {
  // Navigation & View Mode State
  const [viewMode, setViewMode] = useState<VotingViewMode>('list');
  const [selectedCategory, setSelectedCategory] = useState<VotingCategoryItem | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<MatchVotingItem | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<VotingCategoryItem | null>(null);
  const [matchToDelete, setMatchToDelete] = useState<MatchVotingItem | null>(null);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [showDeleteCategory, setShowDeleteCategory] = useState(false);
  const categoryReturnViewMode = useRef<VotingViewMode>('list');
  const isCategoryView = viewMode === 'category';
  const fieldsDisabled = isCategoryView && !isEditingCategory;
  const [isVotingEnabled, setIsVotingEnabled] = useState<boolean>(false);
  const [isSharedVotingGateBlocked, setIsSharedVotingGateBlocked] = useState(false);
  const [votingControlState, setVotingControlState] = useState<VotingControlState>({
    mode: 'off',
    enabledCount: 0,
    totalCount: 0,
    failedIds: [],
  });
  const [selectedFilter, setSelectedFilter] = useState<DropdownOption>(filterOptions[0]);

  const activeFilterKey = selectedFilter.value as VotingFilterValue;
  const activeFilterLabel = activeFilterKey === 'matches' ? 'Match' : 'Tournament';
  const nextCategoryId = useRef<VotingFilterState<number>>({ tournaments: 0, matches: 0 });
  const [categoryPlayers, setCategoryPlayers] = useState<VotingFilterState<Record<string, VotingPlayer[]>>>({
    tournaments: {},
    matches: {},
  });
  const [categoryQuestionIds, setCategoryQuestionIds] = useState<VotingFilterState<Record<string, string>>>({
    tournaments: {},
    matches: {},
  });

  const [categories, setCategories] = useState<VotingFilterState<VotingCategoryItem[]>>({
    tournaments: [],
    matches: [],
  });
  const [votingCampaigns, setVotingCampaigns] = useState<VotingFilterState<EngagementCampaign | null>>({
    tournaments: null,
    matches: null,
  });
  const tournamentVotingCampaigns = useRef<EngagementCampaign[]>([]);
  const activeVotingCampaign = votingCampaigns[activeFilterKey];
  const activeCategories = activeFilterKey === 'matches' && selectedMatch
    ? categories.matches.filter(category => isCategoryForMatch(category, selectedMatch))
    : categories[activeFilterKey];
  const activeCategoryPlayers = categoryPlayers[activeFilterKey];

  const nextMatchId = useRef(0);
  const [matches, setMatches] = useState<MatchVotingItem[]>([]);
  const [availableVotingPlayers, setAvailableVotingPlayers] = useState<VotingPlayer[]>([]);
  const [isLoadingAvailablePlayers, setIsLoadingAvailablePlayers] = useState(false);
  const [availablePlayersError, setAvailablePlayersError] = useState('');
  const [isSavingPlayers, setIsSavingPlayers] = useState(false);
  const [isLoadingCategoryDetails, setIsLoadingCategoryDetails] = useState(false);
  const [isUpdatingToggle, setIsUpdatingToggle] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [awardCategoryOptions, setAwardCategoryOptions] = useState<DropdownOption[]>([]);
  const [createdAwardCategoryOptions, setCreatedAwardCategoryOptions] = useState<DropdownOption[]>([]);
  const [apiAgeCategoryOptions, setApiAgeCategoryOptions] = useState<DropdownOption[]>([]);
  const [awardCategoriesError, setAwardCategoriesError] = useState('');
  const [ageCategoriesError, setAgeCategoriesError] = useState('');
  const [isLoadingAwardCategories, setIsLoadingAwardCategories] = useState(false);
  const [isLoadingAgeCategories, setIsLoadingAgeCategories] = useState(false);
  const [availableMatchFixtures, setAvailableMatchFixtures] = useState<Fixture[]>([]);
  const [isLoadingMatchFixtures, setIsLoadingMatchFixtures] = useState(false);
  const [matchFixturesError, setMatchFixturesError] = useState('');
  const [isSavingMatch, setIsSavingMatch] = useState(false);

  const availableAwardCategoryOptions = getUniqueOptions([...awardCategoryOptions, ...createdAwardCategoryOptions]);
  const ageCategoryDropdownOptions = ageCategoryOptionsList && ageCategoryOptionsList.length > 0
    ? ageCategoryOptionsList
    : apiAgeCategoryOptions;
  const matchOptions = availableMatchFixtures.map(mapFixtureToMatchOption);

  const loadVotingData = async (activeSeasonId: string) => {
    const [seasonCampaigns, matchCampaigns, settingResponse] = await Promise.all([
      listAllVotingCampaigns({
        limit: 100,
        type: 'voting',
        scope: 'season',
        docType: 'season',
        seasonId: activeSeasonId,
      }),
      listAllVotingCampaigns({
        limit: 100,
        type: 'voting',
        scope: 'match',
        docType: 'match',
        seasonId: activeSeasonId,
      }),
      getEngagementSetting('season', activeSeasonId).catch(() => null),
    ]);
    const campaigns = [...seasonCampaigns, ...matchCampaigns];
    const liveVotingCampaigns = getLiveVotingCampaigns(campaigns, activeSeasonId);
    const visibleSeasonCampaigns = liveVotingCampaigns.filter(campaign => getCampaignScope(campaign) === 'season');
    const visibleMatchCampaigns = liveVotingCampaigns.filter(campaign => getCampaignScope(campaign) === 'match');
    const campaignQuestionResponses = await Promise.all(
      [...visibleSeasonCampaigns, ...visibleMatchCampaigns].map(async campaign => {
        const campaignId = getCampaignId(campaign);
        const questionResponse = campaignId ? await listCampaignQuestions(campaignId) : [];
        return {
          campaign,
          questions: Array.isArray(questionResponse) ? questionResponse : [],
        };
      })
    );

    return {
      liveVotingCampaigns,
      visibleSeasonCampaigns,
      visibleMatchCampaigns,
      campaignQuestionResponses,
      sharedGateBlocked: settingResponse?.enabled === false,
    };
  };

  const refreshVotingData = async (failedIds: string[] = []) => {
    if (!seasonId) return;
    const loadedData = await loadVotingData(seasonId);
    const visibleSeasonQuestionEntries = loadedData.campaignQuestionResponses.filter(({ campaign }) => getCampaignScope(campaign) === 'season').flatMap(({ campaign, questions }) => (
      questions.map(question => ({ campaign, question }))
    ));
    const visibleMatchQuestionEntries = loadedData.campaignQuestionResponses.filter(({ campaign }) => getCampaignScope(campaign) === 'match').flatMap(({ campaign, questions }) => (
      questions.map(question => ({ campaign, question }))
    ));

    tournamentVotingCampaigns.current = loadedData.visibleSeasonCampaigns;
    const controlState = getVotingControlState(loadedData.liveVotingCampaigns, failedIds);
    setVotingControlState(controlState);
    setIsVotingEnabled(controlState.mode === 'on');
    setIsSharedVotingGateBlocked(loadedData.sharedGateBlocked);
    setVotingCampaigns(previous => ({
      ...previous,
      tournaments: loadedData.visibleSeasonCampaigns[0] ?? null,
      matches: loadedData.visibleMatchCampaigns[0] ?? null,
    }));
    setCategories(() => ({
      tournaments: visibleSeasonQuestionEntries.map(({ question, campaign }) => mapQuestionToCategory(question, campaign)),
      matches: visibleMatchQuestionEntries.map(({ question, campaign }) => mapQuestionToCategory(question, campaign)),
    }));
    setCategoryPlayers(() => ({
      tournaments: Object.fromEntries(visibleSeasonQuestionEntries.map(({ question }) => [getQuestionId(question), mapQuestionOptionsToPlayers(question)])),
      matches: Object.fromEntries(visibleMatchQuestionEntries.map(({ question }) => [getQuestionId(question), mapQuestionOptionsToPlayers(question)])),
    }));
    setCategoryQuestionIds(() => ({
      tournaments: Object.fromEntries(visibleSeasonQuestionEntries.map(({ question }) => [getQuestionId(question), getQuestionId(question)])),
      matches: Object.fromEntries(visibleMatchQuestionEntries.map(({ question }) => [getQuestionId(question), getQuestionId(question)])),
    }));
    setMatches(loadedData.visibleMatchCampaigns.map(mapCampaignToMatch));
  };

  useEffect(() => {
    let isMounted = true;

    const loadAwardCategories = async () => {
      setIsLoadingAwardCategories(true);
      setAwardCategoriesError('');
      try {
        const response = await listAwardCategories({ seasonId, tournamentId, sport: tournamentSports || '' });
        if (!isMounted) return;
        const awardCategories = Array.isArray(response) ? response : [];
        const activeAwardCategories = awardCategories.filter(awardCategory => awardCategory.enabled !== false && awardCategory.status?.toLowerCase() !== 'archived');
        setAwardCategoryOptions(
          activeAwardCategories
            .map(mapAwardCategoryToOption)
            .filter(option => option.value)
        );
      } catch (error: any) {
        if (!isMounted) return;
        setAwardCategoryOptions([]);
        setAwardCategoriesError(getApiErrorMessage(error, 'Failed to load award categories.'));
      } finally {
        if (isMounted) setIsLoadingAwardCategories(false);
      }
    };

    loadAwardCategories();

    return () => {
      isMounted = false;
    };
  }, [seasonId, tournamentId, tournamentSports]);

  useEffect(() => {
    let isMounted = true;

    const loadAgeCategories = async () => {
      if (!tournamentId) {
        setApiAgeCategoryOptions([]);
        setAgeCategoriesError('');
        return;
      }

      setIsLoadingAgeCategories(true);
      setAgeCategoriesError('');
      try {
        const response = await getAllAgeCategoriesPaginated(
          tournamentId,
          1,
          100,
          '',
          '',
          '',
          seasonId,
          tournamentSports || 'football'
        );
        if (!isMounted) return;
        const ageCategories = response?.paginatedResults ?? response?.results ?? response?.data ?? (Array.isArray(response) ? response : []);
        setApiAgeCategoryOptions(
          getUniqueOptions(
            ageCategories
              .map(mapAgeCategoryToOption)
              .filter((option: DropdownOption | null): option is DropdownOption => Boolean(option?.value && option?.label))
          )
        );
      } catch (error: any) {
        if (!isMounted) return;
        setApiAgeCategoryOptions([]);
        setAgeCategoriesError(getApiErrorMessage(error, 'Failed to load age categories.'));
      } finally {
        if (isMounted) setIsLoadingAgeCategories(false);
      }
    };

    loadAgeCategories();

    return () => {
      isMounted = false;
    };
  }, [seasonId, tournamentId, tournamentSports]);

  useEffect(() => {
    let isMounted = true;

    const loadTournamentVotingCampaigns = async () => {
      if (!seasonId) {
        setVotingCampaigns(previous => ({
          ...previous,
          tournaments: null,
          matches: null,
        }));
        setCategories(previous => ({
          ...previous,
          tournaments: [],
          matches: [],
        }));
        setCategoryPlayers(previous => ({
          ...previous,
          tournaments: {},
          matches: {},
        }));
        setCategoryQuestionIds(previous => ({
          ...previous,
          tournaments: {},
          matches: {},
        }));
        setMatches([]);
        tournamentVotingCampaigns.current = [];
        setIsSharedVotingGateBlocked(false);
        setVotingControlState({ mode: 'off', enabledCount: 0, totalCount: 0, failedIds: [] });
        setIsVotingEnabled(false);
        setCategoriesError('');
        setIsLoadingCategories(false);
        return;
      }

      setIsLoadingCategories(true);
      setCategoriesError('');
      try {
        const loadedData = await loadVotingData(seasonId);
        if (!isMounted) return;
        const visibleSeasonQuestionEntries = loadedData.campaignQuestionResponses.filter(({ campaign }) => isVisibleSeasonVotingCampaign(campaign)).flatMap(({ campaign, questions }) => (
          questions.map(question => ({ campaign, question }))
        ));
        const visibleMatchQuestionEntries = loadedData.campaignQuestionResponses.filter(({ campaign }) => isVisibleMatchVotingCampaign(campaign)).flatMap(({ campaign, questions }) => (
          questions.map(question => ({ campaign, question }))
        ));
        tournamentVotingCampaigns.current = loadedData.visibleSeasonCampaigns;
        const controlState = getVotingControlState(loadedData.liveVotingCampaigns);

        setVotingControlState(controlState);
        setVotingCampaigns(previous => ({
          ...previous,
          tournaments: loadedData.visibleSeasonCampaigns[0] ?? null,
          matches: loadedData.visibleMatchCampaigns[0] ?? null,
        }));
        setCategories(previous => ({
          ...previous,
          tournaments: visibleSeasonQuestionEntries.map(({ question, campaign }) => mapQuestionToCategory(question, campaign)),
          matches: visibleMatchQuestionEntries.map(({ question, campaign }) => mapQuestionToCategory(question, campaign)),
        }));
        setCategoryPlayers(previous => ({
          ...previous,
          tournaments: Object.fromEntries(visibleSeasonQuestionEntries.map(({ question }) => [getQuestionId(question), mapQuestionOptionsToPlayers(question)])),
          matches: Object.fromEntries(visibleMatchQuestionEntries.map(({ question }) => [getQuestionId(question), mapQuestionOptionsToPlayers(question)])),
        }));
        setCategoryQuestionIds(previous => ({
          ...previous,
          tournaments: Object.fromEntries(visibleSeasonQuestionEntries.map(({ question }) => [getQuestionId(question), getQuestionId(question)])),
          matches: Object.fromEntries(visibleMatchQuestionEntries.map(({ question }) => [getQuestionId(question), getQuestionId(question)])),
        }));
        setMatches(loadedData.visibleMatchCampaigns.map(mapCampaignToMatch));
        setIsVotingEnabled(controlState.mode === 'on');
        setIsSharedVotingGateBlocked(loadedData.sharedGateBlocked);
      } catch (error: any) {
        if (!isMounted) return;
        setIsVotingEnabled(false);
        setVotingControlState(previous => ({ ...previous, mode: 'off', failedIds: [] }));
        setCategoriesError(getApiErrorMessage(error, 'Failed to load voting categories.'));
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    loadTournamentVotingCampaigns();

    return () => {
      isMounted = false;
    };
  }, [seasonId]);

  useEffect(() => {
    if (!selectedCategory || (viewMode !== 'players' && viewMode !== 'category')) return;

    let isMounted = true;
    const isTeamLevel = selectedCategory.level.toLowerCase() === 'team';

    const loadAvailableOptions = async () => {
      setIsLoadingAvailablePlayers(true);
      setAvailablePlayersError('');
      try {
        const response = isTeamLevel
          ? await listTeams({
            page: 1,
            pageSize: 500,
            tournamentId: tournamentId || '',
            tournamentSeason: seasonId || undefined,
            sport: tournamentSports || '',
          })
          : await listPlayers({
            page: 1,
            pageSize: 500,
            tournament: tournamentId || '',
            tournamentSeason: seasonId || undefined,
            sport: tournamentSports || '',
          });

        if (!isMounted) return;

        const rawItems = Array.isArray(response?.paginatedResults)
          ? response.paginatedResults
          : Array.isArray(response?.data?.paginatedResults)
            ? response.data.paginatedResults
            : Array.isArray(response)
              ? response
              : [];
        const totalCount = response?.totalCount ?? response?.data?.totalCount;

        setAvailableVotingPlayers(
          rawItems
            .map(isTeamLevel ? mapApiTeamToVotingPlayer : mapApiPlayerToVotingPlayer)
            .filter((item: VotingPlayer) => item.id)
        );
        if (typeof totalCount === 'number' && totalCount > rawItems.length) {
          setAvailablePlayersError(`Showing first ${rawItems.length} ${isTeamLevel ? 'teams' : 'players'}. Refine the source list or ask backend for pagination support here.`);
        }
        setCategoryQuestionIds(previous => ({
          ...previous,
          [activeFilterKey]: {
            ...previous[activeFilterKey],
            [selectedCategory.id]: selectedCategory.id,
          },
        }));
      } catch (error: any) {
        if (!isMounted) return;
        setAvailablePlayersError(getApiErrorMessage(error, `Failed to load ${isTeamLevel ? 'teams' : 'players'}.`));
      } finally {
        if (isMounted) setIsLoadingAvailablePlayers(false);
      }
    };

    loadAvailableOptions();

    return () => {
      isMounted = false;
    };
  }, [selectedCategory?.id, selectedCategory?.ageCategory, selectedCategory?.level, viewMode, tournamentId, seasonId, tournamentSports, activeFilterKey]);


  // Form State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerImageUploadPromiseRef = useRef<Promise<string> | null>(null);
  const bannerImagePreviewUrlRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    bannerImage: null as File | null,
    bannerImagePreview: '' as string,
    bannerImageKey: '' as string,
    playerTeamLevel: playerTeamLevelOptions[0],
    awardCategory: null as DropdownOption | null,
    name: '',
    ageCategory: null as DropdownOption | null,
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    sortNumber: '1',
    videoUrl: '',
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isUploadingBannerImage, setIsUploadingBannerImage] = useState(false);
  const [matchFormData, setMatchFormData] = useState({
    match: null as DropdownOption | null,
    ageCategory: null as DropdownOption | null,
    matchDate: '',
    startTime: '',
    endTime: '',
  });
  const [matchFormErrors, setMatchFormErrors] = useState<{ [key: string]: string }>({});

  const revokeBannerImagePreviewUrl = () => {
    if (!bannerImagePreviewUrlRef.current) return;
    URL.revokeObjectURL(bannerImagePreviewUrlRef.current);
    bannerImagePreviewUrlRef.current = null;
  };

  useEffect(() => {
    return () => {
      revokeBannerImagePreviewUrl();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMatchFixtures = async () => {
      if (activeFilterKey !== 'matches' || viewMode !== 'add-match' || !tournamentId || !matchFormData.ageCategory?.value) {
        setAvailableMatchFixtures([]);
        setMatchFixturesError('');
        return;
      }

      setIsLoadingMatchFixtures(true);
      setMatchFixturesError('');
      try {
        const response = await listFixtures({
          tournamentId,
          tournamentSeason: seasonId,
          sport: tournamentSports || '',
          ageCategory: matchFormData.ageCategory.value,
          page: 1,
          pageSize: 500,
        });
        if (!isMounted) return;
        setAvailableMatchFixtures(response?.paginatedResults ?? []);
      } catch (error: any) {
        if (!isMounted) return;
        setAvailableMatchFixtures([]);
        setMatchFixturesError(getApiErrorMessage(error, 'Failed to load matches.'));
      } finally {
        if (isMounted) setIsLoadingMatchFixtures(false);
      }
    };

    loadMatchFixtures();

    return () => {
      isMounted = false;
    };
  }, [activeFilterKey, viewMode, tournamentId, seasonId, tournamentSports, matchFormData.ageCategory?.value]);

  // Image Upload Handlers
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be below 5MB');
        return;
      }
      revokeBannerImagePreviewUrl();
      const previewUrl = URL.createObjectURL(file);
      bannerImagePreviewUrlRef.current = previewUrl;
      setFormData((prev) => ({
        ...prev,
        bannerImage: file,
        bannerImagePreview: previewUrl,
        bannerImageKey: '',
      }));
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.bannerImage;
        return updated;
      });

      const uploadPromise = uploadVotingCategoryBannerImage(file);
      bannerImageUploadPromiseRef.current = uploadPromise;
      setIsUploadingBannerImage(true);

      uploadPromise
        .then((uploadedImage) => {
          if (bannerImageUploadPromiseRef.current !== uploadPromise) return;
          setFormData((prev) => ({
            ...prev,
            bannerImageKey: uploadedImage,
          }));
        })
        .catch((error: any) => {
          if (bannerImageUploadPromiseRef.current !== uploadPromise) return;
          setFormErrors((prev) => ({
            ...prev,
            bannerImage: getApiErrorMessage(error, 'Failed to upload image.'),
          }));
        })
        .finally(() => {
          if (bannerImageUploadPromiseRef.current !== uploadPromise) return;
          bannerImageUploadPromiseRef.current = null;
          setIsUploadingBannerImage(false);
        });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleAwardCategoryChange = (option: DropdownOption | null) => {
    setFormData((prev) => ({
      ...prev,
      awardCategory: option,
      name: option?.label || prev.name,
    }));
    setFormErrors((prev) => {
      const updated = { ...prev };
      delete updated.awardCategory;
      delete updated.name;
      return updated;
    });
  };

  const handleCreateAwardCategory = (inputValue: string) => {
    const label = inputValue.trim();
    if (!label) return;

    const createdOption: DropdownOption = {
      value: `new:${label}`,
      label,
      __isNew__: true,
    };

    setCreatedAwardCategoryOptions(previous => getUniqueOptions([...previous, createdOption]));
    handleAwardCategoryChange(createdOption);
  };

  const resetForm = () => {
    revokeBannerImagePreviewUrl();
    setFormData({
      bannerImage: null,
      bannerImagePreview: '',
      bannerImageKey: '',
      playerTeamLevel: playerTeamLevelOptions[0],
      awardCategory: null,
      name: '',
      ageCategory: null,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      sortNumber: '1',
      videoUrl: '',
    });
    setFormErrors({});
  };

  const resetMatchForm = () => {
    setMatchFormData({
      match: null,
      ageCategory: null,
      matchDate: '',
      startTime: '',
      endTime: '',
    });
    setMatchFormErrors({});
  };

  const handleMatchInputChange = (field: string, value: any) => {
    setMatchFormData((prev) => {
      if (field === 'ageCategory') {
        return {
          ...prev,
          ageCategory: value,
          match: null,
          matchDate: '',
          startTime: '',
          endTime: '',
        };
      }

      if (field === 'match') {
        const fixture = value?.fixture as Fixture | undefined;
        return {
          ...prev,
          match: value,
          matchDate: toDateInputValue(fixture?.date || fixture?.dateTime || fixture?.startTime),
          startTime: toTimeInputValue(fixture?.startTime || fixture?.dateTime || fixture?.time),
          endTime: toTimeInputValue(fixture?.endTime),
        };
      }

      return { ...prev, [field]: value };
    });
    if (matchFormErrors[field] || field === 'ageCategory' || field === 'match') {
      setMatchFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        if (field === 'ageCategory') {
          delete updated.match;
          delete updated.matchDate;
          delete updated.startTime;
          delete updated.endTime;
        }
        if (field === 'match') {
          delete updated.matchDate;
          delete updated.startTime;
          delete updated.endTime;
        }
        return updated;
      });
    }
  };

  const handleMatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};

    if (!matchFormData.match) errors.match = 'Match is required';
    if (!matchFormData.ageCategory) errors.ageCategory = 'Age category is required';
    if (!matchFormData.matchDate) errors.matchDate = 'Match date is required';
    if (!matchFormData.startTime) errors.startTime = 'Match starting time is required';
    if (!matchFormData.endTime) errors.endTime = 'Match ending time is required';

    if (Object.keys(errors).length > 0) {
      setMatchFormErrors(errors);
      return;
    }

    setIsSavingMatch(true);
    try {
      const matchId = matchFormData.match?.value || '';
      const campaignPayload = {
        name: matchFormData.match?.label || '',
        type: 'voting' as const,
        scope: 'match' as const,
        seasonId,
        tournamentId,
        ageCategoryId: matchFormData.ageCategory?.value || '',
        matchId,
        config: {
          target: 'match',
          tournamentId,
          seasonId,
          matchId,
          matchLabel: matchFormData.match?.label || '',
          matchDate: matchFormData.matchDate,
          matchStartTime: matchFormData.startTime,
          matchEndTime: matchFormData.endTime,
          ageCategoryId: matchFormData.ageCategory?.value || '',
          ageCategoryLabel: matchFormData.ageCategory?.label || '',
        },
        presentation: {},
      };
      const createdCampaign = await createVotingCampaign(campaignPayload);
      const newMatch: MatchVotingItem = {
        id: matchId || `local-match-${nextMatchId.current++}`,
        campaignId: getCampaignId(createdCampaign),
        team: matchFormData.match?.label || '',
        matchValue: matchId,
        ageCategory: matchFormData.ageCategory?.label || '',
        ageCategoryValue: matchFormData.ageCategory?.value || '',
        matchDate: formatDateDisplay(matchFormData.matchDate),
        startTime: matchFormData.startTime,
        endTime: matchFormData.endTime,
        votingStatus: createdCampaign ? getCampaignStatus(createdCampaign) : 'Live',
        categoryEnabled: createdCampaign?.enabled !== false,
      };

      setMatches((previous) => [...previous.filter(match => match.matchValue !== newMatch.matchValue), newMatch]);
      setSelectedMatch(newMatch);
      resetMatchForm();
      setViewMode('match-detail');
    } catch (error: any) {
      setMatchFormErrors((previous) => ({
        ...previous,
        submit: getApiErrorMessage(error, 'Failed to create match voting campaign.'),
      }));
    } finally {
      setIsSavingMatch(false);
    }
  };

  const openMatch = (match: MatchVotingItem) => {
    setSelectedMatch(match);
    setSelectedCategory(null);
    resetForm();
    setViewMode('match-detail');
  };

  const openTimePicker = (event: React.MouseEvent<any>) => {
    try {
      event.currentTarget.showPicker?.();
    } catch {
      // Browsers only allow showPicker from a direct user click.
    }
  };

  const populateCategoryForm = (category: VotingCategoryItem) => {
    setFormData({
      bannerImage: null,
      bannerImagePreview: category.bannerImagePreview || '',
      bannerImageKey: category.bannerImagePreview || '',
      playerTeamLevel: playerTeamLevelOptions.find(option => option.label === category.level) || playerTeamLevelOptions[0],
      awardCategory: category.awardCategoryId ? { value: category.awardCategoryId, label: category.name } : null,
      name: category.name,
      ageCategory: { value: category.ageCategoryValue, label: category.ageCategory },
      startDate: category.startDate.split('/').reverse().join('-'),
      endDate: category.endDate.split('/').reverse().join('-'),
      startTime: category.startTime,
      endTime: category.endTime,
      sortNumber: category.sortNumber,
      videoUrl: category.videoUrl || '',
    });
    setFormErrors({});
  };

  const openCategory = async (category: VotingCategoryItem, edit = false) => {
    categoryReturnViewMode.current = viewMode === 'category'
      ? activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list'
      : viewMode;
    setSelectedCategory(category);
    populateCategoryForm(category);
    setIsEditingCategory(edit);
    setViewMode('category');
  };


  const handleCancel = () => {
    if (isCategoryView && isEditingCategory && selectedCategory) {
      populateCategoryForm(selectedCategory);
      setIsEditingCategory(false);
      return;
    }
    setSelectedCategory(null);
    setViewMode(activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list');
    resetForm();
  };

  const openAddCategory = (sortBaseCount = activeCategories.length) => {
    setSelectedCategory(null);
    setIsEditingCategory(false);
    resetForm();
    setFormData(previous => ({
      ...previous,
      sortNumber: String(sortBaseCount + 1),
    }));
    setViewMode('add');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};

    const shouldCreateAwardCategory = Boolean(formData.awardCategory?.__isNew__);

    if (!formData.awardCategory) errors.awardCategory = 'Award category is required';
    if (!formData.ageCategory) errors.ageCategory = 'Age category is required';
    if (!formData.startDate) errors.startDate = 'Starting date is required';
    if (!formData.endDate) errors.endDate = 'Ending date is required';
    if (!formData.startTime) errors.startTime = 'Starting time is required';
    if (!formData.endTime) errors.endTime = 'Ending time is required';
    if (!formData.sortNumber.trim()) errors.sortNumber = 'Voting sort number is required';
    if (activeFilterKey === 'matches' && !selectedMatch) errors.submit = 'Select a match before adding category.';
    if (activeFilterKey === 'matches' && !formData.bannerImagePreview) errors.bannerImage = 'Banner image is required';
    if (shouldCreateAwardCategory && !tournamentId) errors.awardCategory = 'Tournament ID is required to create award category.';
    if (shouldCreateAwardCategory && !seasonId) errors.awardCategory = 'Tournament season is required to create award category.';
    if (shouldCreateAwardCategory && !formData.bannerImage) errors.bannerImage = 'Banner image is required to create award category.';
    if (formData.bannerImage && !formData.bannerImageKey && !bannerImageUploadPromiseRef.current) {
      errors.bannerImage = 'Image upload failed. Please upload again.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSavingCategory(true);

    try {
      let awardCategoryId = formData.awardCategory?.value || selectedCategory?.awardCategoryId || '';
      let bannerImage = formData.bannerImageKey || formData.bannerImagePreview || '';
      let awardCategoryLabel = formData.awardCategory?.label || formData.name.trim();

      if (formData.bannerImage && !formData.bannerImageKey) {
        const uploadedImage = await bannerImageUploadPromiseRef.current;
        if (!uploadedImage) {
          throw new Error('Image upload failed. Please upload again.');
        }
        bannerImage = uploadedImage;
        setFormData((prev) => ({
          ...prev,
          bannerImageKey: uploadedImage,
        }));
      }

      if (shouldCreateAwardCategory) {
        if (!bannerImage || !tournamentId || !seasonId) {
          throw new Error('Missing required award category details.');
        }

        const createdAwardCategory = await createAwardCategory({
          tournament: tournamentId,
          tournamentSeason: seasonId,
          title: awardCategoryLabel,
          image: bannerImage,
          resultImage: '',
          startDate: toCampaignDateTime(formData.startDate, formData.startTime),
          endDate: toCampaignDateTime(formData.endDate, formData.endTime),
          sortNo: activeCategories.length + 1,
          ageCategories: formData.ageCategory ? [
            {
              ageCategory: formData.ageCategory.label,
              image: bannerImage,
              resultImage: '',
              videoUrl: formData.videoUrl || '',
            },
          ] : [],
        });

        awardCategoryId = getAwardCategoryId(createdAwardCategory);
        awardCategoryLabel = getAwardCategoryName(createdAwardCategory);

        if (!awardCategoryId) {
          throw new Error('Award category was not created.');
        }

        setCreatedAwardCategoryOptions(previous => getUniqueOptions([
          ...previous.filter(option => option.value !== formData.awardCategory?.value),
          { value: awardCategoryId, label: awardCategoryLabel },
        ]));
      }

      const votingStartTime = toCampaignDateTime(formData.startDate, formData.startTime);
      const votingLockTime = toCampaignDateTime(formData.endDate, formData.endTime);
      const questionStatus = getQuestionStatusForDates(votingStartTime, votingLockTime);
      const questionSortNo = Number(formData.sortNumber);

      // Build category item from form data
      const newCategory: VotingCategoryItem = {
        id: isCategoryView && selectedCategory ? selectedCategory.id : `local-${activeFilterKey}-${nextCategoryId.current[activeFilterKey]++}`,
        campaignId: selectedCategory?.campaignId ?? selectedMatch?.campaignId ?? getCampaignId(activeVotingCampaign),
        awardCategoryId,
        matchId: activeFilterKey === 'matches' ? selectedMatch?.id : undefined,
        name: awardCategoryLabel,
        level: formData.playerTeamLevel.label,
        ageCategory: formData.ageCategory?.label || '',
        startDate: formatDateDisplay(formData.startDate),
        endDate: formatDateDisplay(formData.endDate),
        startTime: formData.startTime,
        endTime: formData.endTime,
        sortNumber: formData.sortNumber,
        ageCategoryValue: formData.ageCategory?.value || '',
        votingStatus: getVotingStatus(questionStatus),
        categoryEnabled: isCategoryView && selectedCategory ? selectedCategory.categoryEnabled : true,
        bannerImagePreview: bannerImage || undefined,
        videoUrl: formData.videoUrl || undefined,
        config: selectedCategory?.config,
      };

      const selectedCategoryPlayers = selectedCategory ? activeCategoryPlayers[selectedCategory.id] : undefined;
      const questionLockStatus = getQuestionVotingLockStatus(questionStatus);
      const questionPayload = {
        question: awardCategoryLabel,
        type: 'single_choice' as const,
        ...(selectedCategoryPlayers?.length ? {
          options: selectedCategoryPlayers.map(player => ({
            id: player.id,
            label: player.name,
            meta: {
              team: player.teamName,
              photoUrl: player.profileImage,
            },
          })),
        } : {}),
        ...(awardCategoryId ? { awardCategoryId } : {}),
        scope: activeFilterKey === 'matches' ? 'match' : 'season',
        ...(activeFilterKey === 'matches' && selectedMatch ? { matchId: selectedMatch.matchValue || selectedMatch.id } : {}),
        startTime: votingStartTime,
        lockTime: votingLockTime,
        sortNo: questionSortNo,
        config: {
          target: activeFilterKey === 'matches' ? 'match' : 'tournament',
          tournamentId,
          ...(activeFilterKey === 'matches' && selectedMatch ? { matchId: selectedMatch.matchValue || selectedMatch.id } : {}),
          campaignId: selectedCategory?.campaignId ?? selectedMatch?.campaignId ?? getCampaignId(activeVotingCampaign),
          awardCategoryId,
          awardCategoryLabel,
          ageCategoryId: formData.ageCategory?.value || '',
          ageCategoryLabel: formData.ageCategory?.label || '',
          playerTeamLevel: formData.playerTeamLevel.value,
          bannerImage,
          videoUrl: formData.videoUrl || '',
        },
      };

      const isUpdatingExistingQuestion = Boolean(isCategoryView && selectedCategory && !selectedCategory.id.startsWith('local-'));
      const selectedCategoryStartDate = selectedCategory?.startDate.split('/').reverse().join('-') || '';
      const selectedCategoryEndDate = selectedCategory?.endDate.split('/').reverse().join('-') || '';
      const hasOnlyVotingEndTimeChanged = Boolean(
        isUpdatingExistingQuestion &&
        selectedCategory &&
        !shouldCreateAwardCategory &&
        !formData.bannerImage &&
        formData.playerTeamLevel.label === selectedCategory.level &&
        (formData.awardCategory?.label || '') === selectedCategory.name &&
        (formData.ageCategory?.value || '') === selectedCategory.ageCategoryValue &&
        formData.startDate === selectedCategoryStartDate &&
        formData.startTime === selectedCategory.startTime &&
        formData.sortNumber === selectedCategory.sortNumber &&
        (formData.videoUrl || '') === (selectedCategory.videoUrl || '') &&
        (formData.bannerImagePreview || '') === (selectedCategory.bannerImagePreview || '') &&
        (formData.endDate !== selectedCategoryEndDate || formData.endTime !== selectedCategory.endTime)
      );

      if (hasOnlyVotingEndTimeChanged && selectedCategory) {
        const savedQuestion = await updateCampaignQuestion(selectedCategory.id, {
          lockTime: votingLockTime,
          status: questionLockStatus,
        });
        const savedCategory: VotingCategoryItem = {
          ...selectedCategory,
          endDate: newCategory.endDate,
          endTime: newCategory.endTime,
          votingStatus: getVotingStatusFromSubmission(
            savedQuestion?.submissionStatus ?? selectedCategory.submissionStatus,
            getVotingStatus(savedQuestion?.status ?? questionStatus)
          ),
          categoryEnabled: typeof savedQuestion?.enabled === 'boolean' ? savedQuestion.enabled : selectedCategory.categoryEnabled,
          submissionStatus: savedQuestion?.submissionStatus ?? selectedCategory.submissionStatus,
          canSubmit: savedQuestion?.canSubmit ?? selectedCategory.canSubmit,
          config: savedQuestion?.config ?? selectedCategory.config,
        };
        const returnViewMode = categoryReturnViewMode.current;
        setCategories(previous => ({
          ...previous,
          [activeFilterKey]: previous[activeFilterKey].map(category => category.id === selectedCategory.id ? savedCategory : category),
        }));
        setSelectedCategory(returnViewMode === 'players' || returnViewMode === 'category' ? savedCategory : null);
        setIsEditingCategory(false);
        resetForm();
        setViewMode(returnViewMode);
        return;
      }

      if (activeFilterKey === 'matches') {
        let savedCampaignId = selectedCategory?.campaignId ?? selectedMatch?.campaignId ?? '';
        if (!savedCampaignId && selectedMatch) {
          const createdCampaign = await createVotingCampaign({
            name: selectedMatch.team || awardCategoryLabel,
            type: 'voting' as const,
            scope: 'match' as const,
            seasonId,
            tournamentId,
            ageCategoryId: selectedMatch.ageCategoryValue,
            matchId: selectedMatch.matchValue || selectedMatch.id,
            config: {
              target: 'match',
              tournamentId,
              seasonId,
              matchId: selectedMatch.matchValue || selectedMatch.id,
              matchLabel: selectedMatch.team,
              matchDate: selectedMatch.matchDate,
              matchStartTime: selectedMatch.startTime,
              matchEndTime: selectedMatch.endTime,
              ageCategoryId: selectedMatch.ageCategoryValue,
              ageCategoryLabel: selectedMatch.ageCategory,
            },
            presentation: {},
          });
          savedCampaignId = getCampaignId(createdCampaign);
          setSelectedMatch(previous => previous ? { ...previous, campaignId: savedCampaignId } : previous);
          setMatches(previous => previous.map(match => match.id === selectedMatch.id ? { ...match, campaignId: savedCampaignId } : match));
        }

        if (!savedCampaignId) {
          throw new Error('Match voting campaign was not created.');
        }

        const isUpdatingExistingQuestion = isCategoryView && selectedCategory && !selectedCategory.id.startsWith('local-');
        const savedQuestionResponse = isUpdatingExistingQuestion
          ? await updateCampaignQuestion(selectedCategory.id, {
            ...questionPayload,
            config: {
              ...questionPayload.config,
              campaignId: savedCampaignId,
            },
          })
          : await createCampaignQuestion(savedCampaignId, {
            ...questionPayload,
            config: {
              ...questionPayload.config,
              campaignId: savedCampaignId,
            },
        });
        let savedQuestion = savedQuestionResponse ?? (isUpdatingExistingQuestion && selectedCategory ? {
          id: selectedCategory.id,
          _id: selectedCategory.id,
          enabled: selectedCategory.categoryEnabled,
          ...questionPayload,
          config: {
            ...questionPayload.config,
            campaignId: savedCampaignId,
          },
        } : null);
        const savedQuestionId = getQuestionId(savedQuestion);
        if (!savedQuestionId) {
          throw new Error('Voting question was not saved.');
        }
        const statusQuestion = await updateCampaignQuestion(savedQuestionId, {
          status: questionLockStatus,
        }) ?? {};
        savedQuestion = {
          ...savedQuestion,
          ...statusQuestion,
          options: statusQuestion?.options ?? savedQuestion.options,
          config: statusQuestion?.config ?? savedQuestion.config,
        };
        const savedCategory = {
          ...newCategory,
          id: savedQuestionId,
          campaignId: savedCampaignId,
          categoryEnabled: savedQuestion?.enabled !== false,
          config: savedQuestion?.config ?? questionPayload.config,
          votingStatus: newCategory.votingStatus,
        };
        const returnViewMode = isCategoryView ? categoryReturnViewMode.current : 'match-detail';
        setCategories((previous) => ({
          ...previous,
          matches: isCategoryView && selectedCategory
            ? previous.matches.map(category => category.id === selectedCategory.id ? savedCategory : category)
            : [...previous.matches, savedCategory],
        }));
        setCategoryQuestionIds(previous => ({
          ...previous,
          matches: {
            ...previous.matches,
            [savedCategory.id]: savedCategory.id,
          },
        }));
        setSelectedCategory(returnViewMode === 'players' || returnViewMode === 'category' ? savedCategory : null);
        setIsEditingCategory(false);
        resetForm();
        setViewMode(returnViewMode);
        return;
      }

      const campaignPayload = {
        name: awardCategoryLabel,
        type: 'voting' as const,
        scope: 'season' as const,
        seasonId,
        tournamentId,
        ageCategoryId: formData.ageCategory?.value || '',
        config: {
          target: 'tournament',
          tournamentId,
          ageCategoryId: formData.ageCategory?.value || '',
          ageCategoryLabel: formData.ageCategory?.label || '',
          playerTeamLevel: formData.playerTeamLevel.value,
          videoUrl: formData.videoUrl || '',
        },
        presentation: {
          hero: {
            imageUrl: bannerImage,
            title: awardCategoryLabel,
          },
        },
      };

      if (isCategoryView && selectedCategory) {
        const selectedCampaign = tournamentVotingCampaigns.current.find(campaign => getCampaignId(campaign) === selectedCategory.campaignId) ?? activeVotingCampaign;
        const selectedCampaignId = getCampaignId(selectedCampaign);
        const updatedCampaign = selectedCampaignId
          ? await updateVotingCampaign(selectedCampaignId, {
            presentation: {
              hero: {
                imageUrl: bannerImage,
                title: awardCategoryLabel,
              },
            },
          })
          : null;
        let campaignForSavedCategory = updatedCampaign ?? selectedCampaign;
        if (updatedCampaign) {
          tournamentVotingCampaigns.current = tournamentVotingCampaigns.current.map(campaign => (
            getCampaignId(campaign) === getCampaignId(updatedCampaign) ? updatedCampaign : campaign
          ));
        }
        const savedQuestion = selectedCategory.id.startsWith('local-')
          ? null
          : await updateCampaignQuestion(selectedCategory.id, questionPayload);
        const questionForRow = savedQuestion ?? {
          id: selectedCategory.id,
          _id: selectedCategory.id,
          enabled: selectedCategory.categoryEnabled,
          ...questionPayload,
        };
        let savedQuestionForRow = questionForRow;
        if (!selectedCategory.id.startsWith('local-')) {
          const statusQuestion = await updateCampaignQuestion(selectedCategory.id, {
            status: questionLockStatus,
          }) ?? {};
          savedQuestionForRow = {
            ...questionForRow,
            ...statusQuestion,
            options: statusQuestion?.options ?? questionForRow.options,
            config: statusQuestion?.config ?? questionForRow.config,
          };
        }
        const savedCategory = campaignForSavedCategory ? mapQuestionToCategory(savedQuestionForRow, campaignForSavedCategory) : newCategory;
        setCategories(previous => ({
          ...previous,
          [activeFilterKey]: previous[activeFilterKey].map(category => category.id === savedCategory.id ? savedCategory : category),
        }));
        const returnViewMode = categoryReturnViewMode.current;
        setSelectedCategory(returnViewMode === 'players' || returnViewMode === 'category' ? savedCategory : null);
        setIsEditingCategory(false);
        resetForm();
        setViewMode(returnViewMode);
        return;
      }

      const selectedAgeCategoryId = formData.ageCategory?.value || '';
      const existingCampaign = tournamentVotingCampaigns.current.find(campaign => (
        campaign.scope === 'season' &&
        campaign.status?.toLowerCase() !== 'archived' &&
        (campaign.ageCategoryId ?? campaign.config?.ageCategoryId) === selectedAgeCategoryId
      )) ?? null;
      const campaignId = getCampaignId(existingCampaign);
      const createdCampaign = campaignId ? null : await createVotingCampaign(campaignPayload);
      const updatedExistingCampaign = campaignId
        ? await updateVotingCampaign(campaignId, {
          presentation: campaignPayload.presentation,
        })
        : null;
      const campaign = updatedExistingCampaign ?? createdCampaign;
      const campaignForRow: EngagementCampaign = campaign ?? {
        id: campaignId,
        name: campaignPayload.name,
        type: campaignPayload.type,
        scope: campaignPayload.scope,
        status: 'live',
        config: campaignPayload.config,
        presentation: campaignPayload.presentation,
      };
      const savedCampaignId = getCampaignId(campaignForRow);

      if (!savedCampaignId) {
        throw new Error('Campaign was not created.');
      }
      if (!campaignId) {
        tournamentVotingCampaigns.current = [
          ...tournamentVotingCampaigns.current.filter(existing => getCampaignId(existing) !== savedCampaignId),
          campaignForRow,
        ];
      } else if (updatedExistingCampaign) {
        tournamentVotingCampaigns.current = tournamentVotingCampaigns.current.map(existing => (
          getCampaignId(existing) === campaignId ? updatedExistingCampaign : existing
        ));
      }

      const createdQuestion = await createCampaignQuestion(savedCampaignId, {
        ...questionPayload,
        config: {
          ...questionPayload.config,
          campaignId: savedCampaignId,
        },
      });
      const createdQuestionId = getQuestionId(createdQuestion);
      if (!createdQuestionId) {
        throw new Error('Voting question was not saved.');
      }
      const statusQuestion = await updateCampaignQuestion(createdQuestionId, {
        status: questionLockStatus,
      }) ?? {};
      const savedQuestion = {
        ...createdQuestion,
        ...statusQuestion,
        options: statusQuestion?.options ?? createdQuestion.options,
        config: statusQuestion?.config ?? createdQuestion.config,
      };
      const campaignForSavedCategory = campaignForRow;
      const savedCategory = mapQuestionToCategory(savedQuestion, campaignForSavedCategory);
      setVotingCampaigns(previous => ({
        ...previous,
        [activeFilterKey]: campaignForSavedCategory,
      }));
      setCategoryQuestionIds(previous => ({
        ...previous,
        [activeFilterKey]: {
          ...previous[activeFilterKey],
          [savedCategory.id]: savedCategory.id,
        },
      }));
      setCategories((previous) => ({
        ...previous,
        [activeFilterKey]: [...previous[activeFilterKey], savedCategory],
      }));
      if (seasonId) await refreshVotingData();
      resetForm();
      setViewMode('list');
    } catch (error: any) {
      setFormErrors({
        submit: getApiErrorMessage(error, 'Failed to save voting category.'),
      });
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleVotingToggle = async (enabled: boolean) => {
    if (!seasonId) return;

    setIsUpdatingToggle(true);
    setCategoriesError('');
    try {
      const latestData = await loadVotingData(seasonId);
      const targetCampaigns = latestData.liveVotingCampaigns;

      if (enabled && targetCampaigns.length === 0) {
        await refreshVotingData();
        openAddCategory();
        return;
      }

      const failedIds: string[] = [];
      const campaignsToUpdate = targetCampaigns.filter(campaign => campaign.enabled !== enabled);

      for (const campaign of campaignsToUpdate) {
        const campaignId = getCampaignId(campaign);
        if (!campaignId) continue;
        try {
          await updateVotingCampaign(campaignId, { enabled });
        } catch {
          failedIds.push(campaignId);
        }
      }

      await refreshVotingData(failedIds);
      if (failedIds.length > 0) {
        setCategoriesError(`Updated some voting campaigns, but ${failedIds.length} failed. Retry after refresh.`);
      }
    } catch (error: any) {
      try {
        await refreshVotingData();
      } catch {
        setCategoriesError(getApiErrorMessage(error, 'Failed to update voting campaigns. Refresh to confirm the current state.'));
      }
      setCategoriesError(getApiErrorMessage(error, 'Failed to update voting campaigns.'));
    } finally {
      setIsUpdatingToggle(false);
    }
  };

  const handleCategoryToggle = async (index: number) => {
    const category = activeCategories[index];
    if (!category) return;
    const nextEnabled = !category.categoryEnabled;

    setCategories((previous) => ({
      ...previous,
      [activeFilterKey]: previous[activeFilterKey].map((cat, i) => (
        activeFilterKey === 'matches'
          ? cat.id === category.id
          : i === index
      ) ? { ...cat, categoryEnabled: nextEnabled } : cat),
    }));

    if (!category.id || category.id.startsWith('local-')) return;

    try {
      await updateCampaignQuestion(category.id, { enabled: nextEnabled });
    } catch (error: any) {
      setCategories((previous) => ({
        ...previous,
        [activeFilterKey]: previous[activeFilterKey].map((cat, i) => (
          (activeFilterKey === 'matches' ? cat.id === category.id : i === index) ? { ...cat, categoryEnabled: category.categoryEnabled } : cat
        )),
      }));
      setCategoriesError(getApiErrorMessage(error, 'Failed to update category status.'));
    }
  };

  const handleMatchToggle = async (match: MatchVotingItem) => {
    const matchRowKey = getMatchRowKey(match);
    const nextEnabled = !match.categoryEnabled;

    setMatches((previous) => previous.map(item => (
      getMatchRowKey(item) === matchRowKey ? { ...item, categoryEnabled: nextEnabled } : item
    )));

    if (!match.campaignId || match.campaignId.startsWith('local-')) return;

    try {
      await updateVotingCampaign(match.campaignId, { enabled: nextEnabled });
    } catch (error: any) {
      setMatches((previous) => previous.map(item => (
        getMatchRowKey(item) === matchRowKey ? { ...item, categoryEnabled: match.categoryEnabled } : item
      )));
      setCategoriesError(getApiErrorMessage(error, 'Failed to update match voting status.'));
    }
  };

  const handleDeleteCategory = async (category: VotingCategoryItem) => {
    if (!category) return;

    try {
      if (category.id && !category.id.startsWith('local-')) {
        await updateCampaignQuestion(category.id, getDeletedQuestionPayload(category));
      }
      const remainingCategories = activeCategories.filter(item => item.id !== category.id);
      setCategories((previous) => ({
        ...previous,
        [activeFilterKey]: activeFilterKey === 'matches'
          ? previous.matches.filter(item => item.id !== category.id)
          : remainingCategories,
      }));
      setCategoryPlayers(previous => {
        const updatedPlayers = { ...previous[activeFilterKey] };
        delete updatedPlayers[category.id];
        return {
          ...previous,
          [activeFilterKey]: updatedPlayers,
        };
      });
      setCategoryToDelete(null);
    } catch (error: any) {
      setCategoriesError(getApiErrorMessage(error, 'Failed to delete voting category.'));
    }
  };

  const handleDeleteMatch = async (match: MatchVotingItem) => {
    try {
      if (match.campaignId && !match.campaignId.startsWith('local-')) {
        await updateVotingCampaign(match.campaignId, { action: 'archive' });
      }
      const matchRowKey = getMatchRowKey(match);
      setMatches((previous) => previous.filter(item => getMatchRowKey(item) !== matchRowKey));
      setCategories((previous) => ({
        ...previous,
        matches: previous.matches.filter(category => !isCategoryForMatch(category, match)),
      }));
      setMatchToDelete(null);
    } catch (error: any) {
      setCategoriesError(getApiErrorMessage(error, 'Failed to delete match voting.'));
    }
  };

  const saveCategoryPlayers = async (players: VotingPlayer[]) => {
    if (!selectedCategory) return;
    if (!selectedCategory.id || selectedCategory.id.startsWith('local-')) {
      setCategoryPlayers(previous => ({
        ...previous,
        [activeFilterKey]: {
          ...previous[activeFilterKey],
          [selectedCategory.id]: players,
        },
      }));
      return;
    }

    setIsSavingPlayers(true);
    try {
      setAvailablePlayersError('');
      const questionPayload = buildPlayersQuestionPayload(selectedCategory, players);
      const questionId = categoryQuestionIds[activeFilterKey][selectedCategory.id] || selectedCategory.id;
      const savedQuestion = await updateCampaignQuestion(questionId, questionPayload);
      const activatedStatus = selectedCategory.votingStatus;

      setCategoryQuestionIds(previous => ({
        ...previous,
        [activeFilterKey]: {
          ...previous[activeFilterKey],
          [selectedCategory.id]: questionId,
        },
      }));
      setCategoryPlayers(previous => ({
        ...previous,
        [activeFilterKey]: {
          ...previous[activeFilterKey],
          [selectedCategory.id]: mapQuestionOptionsToPlayers(savedQuestion).length ? mapQuestionOptionsToPlayers(savedQuestion) : players,
        },
      }));
      setSelectedCategory(previous => (
        previous?.id === selectedCategory.id ? { ...previous, votingStatus: activatedStatus } : previous
      ));
    } catch (error: any) {
      setAvailablePlayersError(getApiErrorMessage(error, 'Failed to save voting players.'));
    } finally {
      setIsSavingPlayers(false);
    }
  };

  // Custom Select Style matching design
  const customSelectStyles = {
    singleValue: (base: any) => ({ ...base, color: '#212529' }),
    control: (base: any) => ({
      ...base,
      minHeight: '44px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      fontSize: '14px',
      '&:hover': { borderColor: '#cbd5e1' },
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: '14px',
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f1f5f9' : 'transparent',
      color: state.isSelected ? '#ffffff' : '#334155',
      cursor: 'pointer',
    }),
  };

  if (activeFilterKey === 'matches' && viewMode === 'add-match') {
    const selectedMatchFixture = matchFormData.match?.fixture as Fixture | undefined;
    const hasBackendMatchEndTime = Boolean(toTimeInputValue(selectedMatchFixture?.endTime));
    const isMatchEndTimeDisabled = !matchFormData.match || hasBackendMatchEndTime;

    return (
      <div className="voting-add-match-container" style={{ padding: '0 5px' }}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <button
            onClick={() => { setViewMode('list'); resetMatchForm(); }}
            aria-label="Back to voting matches"
            type="button"
            className="btn btn-light d-flex align-items-center justify-content-center"
            style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: '20px', color: '#000000' }}></i>
          </button>
          <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#000000' }}>
            Voting
          </h4>
        </div>

        <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid #edf2f7' }}>
          <div className="card-body" style={{ padding: '24px' }}>
            <h5 className="mb-4" style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Add New Match</h5>
            <Form onSubmit={handleMatchSubmit}>
              <Row className="mb-4 g-4">
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                    Age Category<span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={matchFormData.ageCategory}
                    onChange={(opt: any) => handleMatchInputChange('ageCategory', opt)}
                    options={ageCategoryDropdownOptions}
                    placeholder="Select Age Category"
                    isSearchable={true}
                    styles={customSelectStyles}
                  />
                  {matchFormErrors.ageCategory && <div className="text-danger small mt-1">{matchFormErrors.ageCategory}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                    Select Match <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={matchFormData.match}
                    onChange={(opt: any) => handleMatchInputChange('match', opt)}
                    options={matchOptions}
                    placeholder={matchFormData.ageCategory ? 'Select Match' : 'Select age category first'}
                    isSearchable={false}
                    isDisabled={!matchFormData.ageCategory || isLoadingMatchFixtures}
                    isLoading={isLoadingMatchFixtures}
                    styles={customSelectStyles}
                  />
                  {matchFormErrors.match && <div className="text-danger small mt-1">{matchFormErrors.match}</div>}
                  {matchFixturesError && <div className="text-danger small mt-1">{matchFixturesError}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                    Match Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={matchFormData.matchDate}
                    disabled
                    isInvalid={!!matchFormErrors.matchDate}
                    style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8fafc' }}
                  />
                  {matchFormErrors.matchDate && <div className="text-danger small mt-1">{matchFormErrors.matchDate}</div>}
                </Col>
              </Row>

              <Row className="mb-4 g-4">
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                    Match Starting Time <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="time"
                    value={matchFormData.startTime}
                    disabled
                    isInvalid={!!matchFormErrors.startTime}
                    style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#f8fafc' }}
                  />
                  {matchFormErrors.startTime && <div className="text-danger small mt-1">{matchFormErrors.startTime}</div>}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                    Match Ending Time <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="time"
                    value={matchFormData.endTime}
                    onChange={(e) => handleMatchInputChange('endTime', e.target.value)}
                    disabled={isMatchEndTimeDisabled}
                    isInvalid={!!matchFormErrors.endTime}
                    style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: isMatchEndTimeDisabled ? '#f8fafc' : '#ffffff' }}
                  />
                  {matchFormErrors.endTime && <div className="text-danger small mt-1">{matchFormErrors.endTime}</div>}
                </Col>
              </Row>

              {matchFormErrors.submit && <div className="text-danger small mb-3">{matchFormErrors.submit}</div>}

              <div className="d-flex justify-content-end align-items-center gap-3 pt-2">
                <Button variant="outline-primary" type="button" disabled={isSavingMatch} onClick={() => { resetMatchForm(); setViewMode('list'); }} style={{ minWidth: '110px', height: '42px', borderRadius: '8px', borderColor: '#3b82f6', color: '#2563eb', fontWeight: 500 }}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSavingMatch} style={{ minWidth: '140px', height: '42px', borderRadius: '8px', backgroundColor: '#0d6efd', borderColor: '#0d6efd', fontWeight: 500 }}>
                  {isSavingMatch ? 'Saving...' : 'Add Match'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    );
  }

  if (activeFilterKey === 'matches' && viewMode === 'match-detail' && selectedMatch) {
    return (
      <div className="voting-match-detail-container" style={{ padding: '0 5px' }}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <button
            onClick={() => { setSelectedMatch(null); setViewMode('list'); resetForm(); }}
            aria-label="Back to voting matches"
            type="button"
            className="btn btn-light d-flex align-items-center justify-content-center"
            style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: '20px', color: '#000000' }}></i>
          </button>
          <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#000000' }}>
            {selectedMatch.team}
          </h4>
        </div>

        <div className="card border-0" style={{ borderRadius: '12px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)', border: '1px solid #edf2f7' }}>
          <div className="card-body" style={{ padding: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
                Match Voting Categories
              </h5>
              <div className="d-flex align-items-center gap-2">
                <Button variant="primary" onClick={() => openAddCategory()} style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd', borderRadius: '8px', padding: '8px 20px', fontWeight: 500, fontSize: '14px' }}>
                  Add Category
                </Button>
              </div>
            </div>

            {activeCategories.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <Table hover responsive className="align-middle mb-0" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      {['Category', 'Level', 'Age Category', 'Voting Start Date', 'Voting End Date', 'Voting Status', 'Category Status', 'Actions'].map((heading) => (
                        <th key={heading} style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeCategories.map((cat, idx) => {
                      const playersReadOnly = !canEditCategoryPlayers(cat);
                      const playerActionLabel = activeCategoryPlayers[cat.id]?.length
                        ? 'Manage'
                        : playersReadOnly ? 'View' : 'Add';

                      return (
                      <tr key={cat.id}>
                        <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: 500 }}>{cat.name}</td>
                        <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.level}</td>
                        <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.ageCategory}</td>
                        <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.startDate}</td>
                        <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.endDate}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <Badge
                            pill
                            title={cat.submissionMessage}
                            style={{ ...statusBadgeStyle[cat.votingStatus], fontSize: '12px', fontWeight: 500, padding: '5px 12px' }}
                          >
                            {cat.votingStatus}
                          </Badge>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <Form.Check type="switch" id={`match-cat-status-${idx}`} checked={cat.categoryEnabled} onChange={() => handleCategoryToggle(idx)} style={{ cursor: 'pointer', marginBottom: 0 }} />
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div className="d-flex align-items-center gap-2">
                            <Button onClick={() => { setSelectedCategory(cat); setViewMode('players'); }} variant="outline-primary" size="sm" style={{ borderRadius: '6px', fontSize: '12px', fontWeight: 500, padding: '4px 12px', borderColor: '#0d6efd' }}>
                              {playerActionLabel}
                            </Button>
                            <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="View" onClick={() => openCategory(cat, false)}>
                              <i className="ri-eye-line" style={{ fontSize: '15px', color: '#64748b' }}></i>
                            </button>
                            <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="Edit" onClick={() => openCategory(cat, true)}>
                              <i className="ri-pencil-line" style={{ fontSize: '15px', color: '#3b82f6' }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted" style={{ padding: '60px 20px', fontSize: '14px' }}>
                No match voting categories available.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'players' && selectedCategory) {
    return (
      <VotingCategoryPlayers
        key={`${activeFilterKey}-${selectedCategory.id}`}
        categoryName={selectedCategory.name}
        players={activeCategoryPlayers[selectedCategory.id] || []}
        availablePlayers={availableVotingPlayers}
        playersLoading={isLoadingAvailablePlayers}
        playersError={availablePlayersError}
        savingPlayers={isSavingPlayers}
        itemType={selectedCategory.level.toLowerCase() === 'team' ? 'team' : 'player'}
        readOnly={!canEditCategoryPlayers(selectedCategory)}
        onChange={saveCategoryPlayers}
        onEditCategory={() => openCategory(selectedCategory, true)}
        onBack={() => {
          setSelectedCategory(null);
          setViewMode(activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list');
        }}
      />
    );
  }

  // -------------------------------------------------------------
  // VIEW 1: ADD CATEGORY FORM
  // -------------------------------------------------------------
  if (viewMode === 'add' || isCategoryView) {
    return (
      <div className="voting-add-category-container" style={{ padding: '0 5px' }}>
        {/* Top Header with Back Arrow */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <button
            onClick={() => {
              setViewMode(activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list');
              setSelectedCategory(null);
              resetForm();
            }}
            aria-label="Back to voting categories"
            type="button"
            className="btn btn-light d-flex align-items-center justify-content-center"
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: '20px', color: '#000000' }}></i>
          </button>
          <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#000000' }}>
            {activeFilterKey === 'matches' && selectedMatch ? selectedMatch.team : 'Voting'}
          </h4>
          {isLoadingCategoryDetails && <span className="text-muted" style={{ fontSize: 14 }}>Loading category...</span>}
        </div>

        {/* Form Card */}
        <div
          className="card border-0"
          style={{
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)',
            border: '1px solid #edf2f7',
          }}
        >
          <div className="card-body" style={{ padding: isCategoryView ? '16px' : '32px' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
                  {isCategoryView ? selectedCategory?.name : `Add New ${activeFilterLabel} Voting Category`}
                </h5>
                {isCategoryView && selectedCategory && (
                  <Badge
                    bg=""
                    title={selectedCategory.submissionMessage}
                    style={{ ...statusBadgeStyle[selectedCategory.votingStatus], fontWeight: 400 }}
                  >
                    {selectedCategory.votingStatus}
                  </Badge>
                )}
              </div>
              {isCategoryView && <div className="d-flex gap-2">
                <Button variant="outline-dark" size="sm" onClick={() => setIsEditingCategory(true)} disabled={isEditingCategory}>Edit <i className="ri-pencil-line" aria-hidden="true" /></Button>
              </div>}
            </div>

            <Form onSubmit={handleSubmit}>
              <fieldset disabled={fieldsDisabled}>
                {/* Row 1: Upload Banner | Player/Team Level | Name */}
                <Row className="mb-4 g-4">
                  {/* Upload Banner Image */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Upload Banner Image <span className="text-danger">*</span>
                    </Form.Label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ display: 'none' }}
                    />
                    <div
                      onClick={() => { if (!fieldsDisabled) fileInputRef.current?.click(); }}
                      style={{
                        height: isCategoryView ? '48px' : '70px',
                        border: '1.5px dashed #60a5fa',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 16px',
                        gap: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {formData.bannerImagePreview ? (
                        <img
                          src={formData.bannerImagePreview}
                          alt="Preview"
                          style={{ height: '48px', width: '48px', objectFit: 'cover', borderRadius: '6px' }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            border: '1px solid #bfdbfe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#2563eb',
                          }}
                        >
                          <i className="ri-image-add-line" style={{ fontSize: '18px' }}></i>
                        </div>
                      )}
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '13px', color: '#1e293b' }}>
                          Upload Image
                        </div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>
                          (jpeg/jpg/png - Below 5Mb)
                        </div>
                      </div>
                    </div>
                    {isUploadingBannerImage && <div className="text-muted small mt-1">Uploading image...</div>}
                    {formErrors.bannerImage && <div className="text-danger small mt-1">{formErrors.bannerImage}</div>}
                  </Col>

                  {/* Player/Team Level */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Player/Team Level <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      isDisabled={fieldsDisabled}
                      classNamePrefix="react-select"
                      value={formData.playerTeamLevel}
                      onChange={(opt: any) => handleInputChange('playerTeamLevel', opt)}
                      options={playerTeamLevelOptions}
                      isSearchable={false}
                      styles={customSelectStyles}
                    />
                  </Col>

                  {/* Award Category */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Award Category <span className="text-danger">*</span>
                    </Form.Label>
                    <CreatableSelect
                      isDisabled={fieldsDisabled || isLoadingAwardCategories}
                      classNamePrefix="react-select"
                      value={formData.awardCategory}
                      onChange={(opt: any) => handleAwardCategoryChange(opt)}
                      onCreateOption={handleCreateAwardCategory}
                      options={availableAwardCategoryOptions}
                      placeholder={isLoadingAwardCategories ? 'Loading Award Categories...' : 'Select or create Award Category'}
                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                      isValidNewOption={(inputValue) => Boolean(inputValue.trim())}
                      isSearchable={true}
                      styles={customSelectStyles}
                    />
                    {formErrors.awardCategory && <div className="text-danger small mt-1">{formErrors.awardCategory}</div>}
                    {awardCategoriesError && <div className="text-danger small mt-1">{awardCategoriesError}</div>}
                    {!isLoadingAwardCategories && !awardCategoriesError && awardCategoryOptions.length === 0 && (
                      <div className="text-muted small mt-1">No award categories found. Type a name to create an option.</div>
                    )}
                  </Col>
                </Row>

                {/* Row 2: Age Category | Voting Starting date | Voting Ending Date */}
                <Row className="mb-4 g-4">
                  {/* Age Category */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Age Category <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      isDisabled={fieldsDisabled || isLoadingAgeCategories || ageCategoryDropdownOptions.length === 0}
                      classNamePrefix="react-select"
                      value={formData.ageCategory}
                      onChange={(opt: any) => handleInputChange('ageCategory', opt)}
                      options={ageCategoryDropdownOptions}
                      placeholder={isLoadingAgeCategories ? 'Loading Age Categories...' : 'Select Age Category'}
                      isSearchable={true}
                      styles={customSelectStyles}
                    />
                    {formErrors.ageCategory && <div className="text-danger small mt-1">{formErrors.ageCategory}</div>}
                    {ageCategoriesError && <div className="text-danger small mt-1">{ageCategoriesError}</div>}
                    {!isLoadingAgeCategories && !ageCategoriesError && ageCategoryDropdownOptions.length === 0 && (
                      <div className="text-danger small mt-1">No age categories available for this tournament.</div>
                    )}
                  </Col>

                  {/* Voting Starting Date */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Voting Starting date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      isInvalid={!!formErrors.startDate}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                  </Col>

                  {/* Voting Ending Date */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Voting Ending Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      isInvalid={!!formErrors.endDate}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                  </Col>
                </Row>

                {/* Row 3: Voting Starting time | Voting Ending Time | Sort Number */}
                <Row className="mb-4 g-4">
                  {/* Voting Starting Time */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Voting Starting time <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.startTime}
                      onClick={openTimePicker}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      isInvalid={!!formErrors.startTime}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                  </Col>

                  {/* Voting Ending Time */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Voting Ending Time <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.endTime}
                      onClick={openTimePicker}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      isInvalid={!!formErrors.endTime}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                  </Col>

                  {/* Sort Number */}
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Voting Sort Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.sortNumber}
                      onChange={(e) => handleInputChange('sortNumber', e.target.value)}
                      isInvalid={!!formErrors.sortNumber}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                    {formErrors.sortNumber && <div className="text-danger small mt-1">{formErrors.sortNumber}</div>}
                  </Col>
                </Row>

                {/* Row 4: Video URL */}
                <Row className="mb-4 g-4">
                  <Col md={4}>
                    <Form.Label className="fw-medium" style={{ fontSize: '14px', color: '#334155' }}>
                      Video URL
                    </Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://example.com/campaign"
                      value={formData.videoUrl}
                      onChange={(e) => handleInputChange('videoUrl', e.target.value)}
                      style={{ minHeight: '44px', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}
                    />
                  </Col>
                </Row>

              </fieldset>
              {formErrors.submit && <div className="alert alert-danger mb-0" role="alert">{formErrors.submit}</div>}
              {/* Footer Action Buttons */}
              {(!isCategoryView || isEditingCategory) && <div className="d-flex justify-content-end align-items-center gap-3 pt-3">
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={handleCancel}
                  disabled={isSavingCategory || isUploadingBannerImage}
                  style={{
                    minWidth: '110px',
                    height: '42px',
                    borderRadius: '8px',
                    borderColor: '#3b82f6',
                    color: '#2563eb',
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSavingCategory}
                  style={{
                    minWidth: '140px',
                    height: '42px',
                    borderRadius: '8px',
                    backgroundColor: '#0d6efd',
                    borderColor: '#0d6efd',
                    fontWeight: 500,
                  }}
                >
                  {isUploadingBannerImage ? 'Uploading...' : isSavingCategory ? 'Saving...' : isCategoryView ? 'Save Changes' : 'Add Category'}
                </Button>
              </div>}
            </Form>
          </div>
        </div>
        {isCategoryView && selectedCategory && <VotingCategoryPlayers
          embedded
          key={`${activeFilterKey}-${selectedCategory.id}`}
          categoryName={selectedCategory.name}
          players={activeCategoryPlayers[selectedCategory.id] || []}
          availablePlayers={availableVotingPlayers}
          playersLoading={isLoadingAvailablePlayers}
          playersError={availablePlayersError}
          savingPlayers={isSavingPlayers}
          itemType={selectedCategory.level.toLowerCase() === 'team' ? 'team' : 'player'}
          readOnly={!canEditCategoryPlayers(selectedCategory)}
          onChange={saveCategoryPlayers}
          onEditCategory={() => openCategory(selectedCategory, true)}
          onBack={() => setViewMode('list')}
        />}
        <VotingRemoveModal category show={showDeleteCategory} onCancel={() => setShowDeleteCategory(false)} onConfirm={async () => {
          if (!selectedCategory) return;
          const categoryId = selectedCategory.id;
          try {
            if (categoryId && !categoryId.startsWith('local-')) {
              await updateCampaignQuestion(categoryId, getDeletedQuestionPayload(selectedCategory));
            }
            const remainingCategories = activeCategories.filter(category => category.id !== categoryId);
            setCategories(previous => ({
              ...previous,
              [activeFilterKey]: activeFilterKey === 'matches'
                ? previous.matches.filter(category => category.id !== categoryId)
                : remainingCategories,
            }));
            setCategoryPlayers(previous => {
              const updatedPlayers = { ...previous[activeFilterKey] };
              delete updatedPlayers[categoryId];
              return {
                ...previous,
                [activeFilterKey]: updatedPlayers,
              };
            });
            setShowDeleteCategory(false);
            setSelectedCategory(null);
            setViewMode(activeFilterKey === 'matches' && selectedMatch ? 'match-detail' : 'list');
            resetForm();
          } catch (error: any) {
            setFormErrors({ submit: getApiErrorMessage(error, 'Failed to delete voting category.') });
          }
        }} />
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW 2: LIST VIEW — Table or Empty State
  // -------------------------------------------------------------
  return (
    <>
    <div className="voting-management-container" style={{ padding: '0 5px' }}>
      {/* Top Header & Controls */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        {/* Left Side: Voting Title & Toggle Switch */}
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0 fw-bold" style={{ fontSize: '24px', color: '#0f172a' }}>
            Voting
          </h4>
          <Form.Check
            type="switch"
            id="voting-status-switch"
            checked={isVotingEnabled}
            onChange={(e) => handleVotingToggle(e.target.checked)}
            disabled={isUpdatingToggle || isLoadingCategories || !seasonId}
            style={{ transform: 'scale(1.15)', cursor: 'pointer', marginBottom: 0 }}
          />
          {votingControlState.mode === 'mixed' && (
            <Badge bg="warning" text="dark">
              Mixed {votingControlState.enabledCount}/{votingControlState.totalCount}
            </Badge>
          )}
          {votingControlState.mode === 'off' && votingControlState.totalCount === 0 && (
            <span className="text-muted small">Set up voting</span>
          )}
          {isUpdatingToggle && <span className="text-muted small">Saving...</span>}
          {!isUpdatingToggle && isLoadingCategories && <span className="text-muted small">Loading...</span>}
        </div>

        {/* Right Side: Dropdown */}
        <div className="d-flex align-items-center gap-3">
          <div style={{ minWidth: '160px' }}>
            <Select
              classNamePrefix="react-select"
              value={selectedFilter}
              onChange={(option) => {
                if (option) {
                  setSelectedFilter(option);
                  setSelectedCategory(null);
                  setSelectedMatch(null);
                  setViewMode('list');
                  resetForm();
                }
              }}
              options={filterOptions}
              isSearchable={false}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  borderRadius: '8px',
                  borderColor: '#e5e7eb',
                  boxShadow: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '14px',
                  backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f1f5f9' : 'transparent',
                  color: state.isSelected ? '#ffffff' : '#334155',
                  cursor: 'pointer',
                }),
              }}
            />
          </div>
        </div>
      </div>
      {categoriesError && <div className="text-danger small mb-3">{categoriesError}</div>}
      {isSharedVotingGateBlocked && <div className="text-warning small mb-3">Shared season engagement is disabled and may block voting submissions or campaign creation.</div>}
      {votingControlState.failedIds.length > 0 && (
        <div className="text-danger small mb-3">
          Failed campaign updates: {votingControlState.failedIds.join(', ')}
        </div>
      )}

      {/* Main Content Card */}
      <div
        className="card border-0"
        style={{
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
          border: '1px solid #edf2f7',
        }}
      >
        <div className="card-body" style={{ padding: '24px' }}>
          {/* Card Header: Title + Add Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
              {activeFilterKey === 'matches' ? 'Matches' : `${activeFilterLabel} Voting Categories`}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="primary"
                onClick={() => { activeFilterKey === 'matches' ? setViewMode('add-match') : openAddCategory(); }}
                style={{
                  backgroundColor: '#0d6efd',
                  borderColor: '#0d6efd',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: 500,
                  fontSize: '14px',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                }}
              >
                {activeFilterKey === 'matches' ? 'Add Match' : 'Add Category'}
              </Button>
            </div>
          </div>

          {/* Conditionally show table or empty state */}
          {activeFilterKey === 'matches' ? (
            matches.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <Table hover responsive className="align-middle mb-0" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      {['Team', 'Age Category', 'Match Date', 'Number of Categories', 'Voting Status', 'Category Status', 'Actions'].map((heading) => (
                        <th key={heading} style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((match, idx) => {
                      const matchRowKey = getMatchRowKey(match);
                      const categoryCount = categories.matches.filter(category => isCategoryForMatch(category, match)).length;
                      return (
                        <tr key={matchRowKey}>
                          <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: 500 }}>{match.team}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{match.ageCategory}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{match.matchDate}</td>
                          <td style={{ padding: '14px 16px', color: '#334155' }}>{categoryCount}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <Badge pill style={{ ...statusBadgeStyle[match.votingStatus], fontSize: '12px', fontWeight: 500, padding: '5px 12px' }}>{match.votingStatus}</Badge>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <Form.Check
                              type="switch"
                              id={`match-status-${idx}`}
                              checked={match.categoryEnabled}
                              onChange={() => handleMatchToggle(match)}
                              style={{ cursor: 'pointer', marginBottom: 0 }}
                            />
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                onClick={() => {
                                  setSelectedMatch(match);
                                  openAddCategory(categories.matches.filter(category => isCategoryForMatch(category, match)).length);
                                }}
                                variant="outline-primary"
                                size="sm"
                                style={{ borderRadius: '6px', fontSize: '12px', fontWeight: 500, padding: '4px 12px', borderColor: '#0d6efd' }}
                              >
                                Add Category
                              </Button>
                              <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="View" onClick={() => openMatch(match)}>
                                <i className="ri-eye-line" style={{ fontSize: '15px', color: '#64748b' }}></i>
                              </button>
                              <button className="btn btn-sm p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }} title="Edit" onClick={() => openMatch(match)}>
                                <i className="ri-pencil-line" style={{ fontSize: '15px', color: '#3b82f6' }}></i>
                              </button>
                              <button
                                className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                                style={{ width: '30px', height: '30px', border: '1px solid #fecaca', borderRadius: '6px', backgroundColor: '#fff' }}
                                title="Delete"
                                onClick={() => setMatchToDelete(match)}
                              >
                                <i className="ri-delete-bin-line" style={{ fontSize: '15px', color: '#ef4444' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: '380px', padding: '60px 20px' }}>
                <div className="mb-3" style={{ color: '#64748b' }}>
                  <svg width="58" height="58" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                    <path d="M4 22h16" />
                    <path d="M10 14.66V17c0 .55-.45 1-1 1H8c-.55 0-1 .45-1 1v1c0 .55.45 1 1 1h8c.55 0 1-.45 1-1v-1c0-.55-.45-1-1-1h-1c-.55 0-1-.45-1-1v-2.34" />
                    <path d="M18 4H6v6c0 3.31 2.69 6 6 6s6-2.69 6-6V4z" />
                  </svg>
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1e293b', fontSize: '18px' }}>
                  No Match voting available
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '14px', maxWidth: '440px' }}>
                  There are no upcoming match voting at the moment.
                </p>
              </div>
            )
          ) : isLoadingCategories && activeFilterKey === 'tournaments' ? (
            <div className="text-center text-muted" style={{ padding: '60px 20px', fontSize: '14px' }}>
              Loading voting categories...
            </div>
          ) : categoriesError && activeFilterKey === 'tournaments' ? (
            <div className="alert alert-danger mb-0" role="alert">
              {categoriesError}
            </div>
          ) : activeCategories.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive className="align-middle mb-0" style={{ fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Category</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Level</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Age Category</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Voting Start Date</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Voting End Date</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Voting Status</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Category Status</th>
                    <th style={{ fontWeight: 600, color: '#475569', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCategories.map((cat, idx) => {
                    const playersReadOnly = !canEditCategoryPlayers(cat);
                    const playerActionLabel = activeCategoryPlayers[cat.id]?.length
                      ? 'Manage'
                      : playersReadOnly ? 'View' : 'Add';

                    return (
                    <tr key={idx}>
                      <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: 500 }}>{cat.name}</td>
                      <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.level}</td>
                      <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.ageCategory}</td>
                      <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.startDate}</td>
                      <td style={{ padding: '14px 16px', color: '#334155' }}>{cat.endDate}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <Badge
                          pill
                          title={cat.submissionMessage}
                          style={{
                            ...statusBadgeStyle[cat.votingStatus],
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '5px 12px',
                          }}
                        >
                          {cat.votingStatus}
                        </Badge>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <Form.Check
                          type="switch"
                          id={`cat-status-${idx}`}
                          checked={cat.categoryEnabled}
                          onChange={() => handleCategoryToggle(idx)}
                          style={{ cursor: 'pointer', marginBottom: 0 }}
                        />
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div className="d-flex align-items-center gap-2">
                          {/* Add / Manage button */}
                          <Button
                            onClick={() => {
                              setSelectedCategory(cat);
                              setViewMode('players');
                            }}
                            variant="outline-primary"
                            size="sm"
                            style={{
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                              padding: '4px 12px',
                              borderColor: '#0d6efd',
                            }}
                          >
                            {playerActionLabel}
                          </Button>
                          {/* View icon */}
                          <button
                            className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }}
                            title="View"
                            onClick={() => openCategory(cat, false)}
                          >
                            <i className="ri-eye-line" style={{ fontSize: '15px', color: '#64748b' }}></i>
                          </button>
                          {/* Edit icon */}
                          <button
                            className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                            style={{ width: '30px', height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff' }}
                            title="Edit"
                            onClick={() => openCategory(cat, true)}
                          >
                            <i className="ri-pencil-line" style={{ fontSize: '15px', color: '#3b82f6' }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            /* Empty State */
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center"
              style={{ minHeight: '380px', padding: '60px 20px' }}
            >
              <div className="mb-3" style={{ color: '#64748b' }}>
                <svg
                  width="58"
                  height="58"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                  <path d="M4 22h16" />
                  <path d="M10 14.66V17c0 .55-.45 1-1 1H8c-.55 0-1 .45-1 1v1c0 .55.45 1 1 1h8c.55 0 1-.45 1-1v-1c0-.55-.45-1-1-1h-1c-.55 0-1-.45-1-1v-2.34" />
                  <path d="M18 4H6v6c0 3.31 2.69 6 6 6s6-2.69 6-6V4z" />
                </svg>
              </div>
              <h5
                className="fw-bold mb-2"
                style={{ color: '#1e293b', fontSize: '18px', letterSpacing: '-0.01em' }}
              >
                No {selectedFilter.value === 'matches' ? 'matches' : 'tournaments'} voting available
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: '14px', maxWidth: '440px' }}>
                There are no upcoming {selectedFilter.value === 'matches' ? 'matches' : 'tournaments'} voting at the moment.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    <VotingRemoveModal
      category
      show={!!categoryToDelete}
      onCancel={() => setCategoryToDelete(null)}
      onConfirm={() => {
        if (categoryToDelete) handleDeleteCategory(categoryToDelete);
      }}
    />
    <VotingRemoveModal
      show={!!matchToDelete}
      title="Delete Match Voting?"
      message="Are you sure you want to delete this match voting?"
      confirmLabel="Yes, Delete"
      onCancel={() => setMatchToDelete(null)}
      onConfirm={() => {
        if (matchToDelete) handleDeleteMatch(matchToDelete);
      }}
    />
    </>
  );
};

export default VotingManagement;
